import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Button as MUIButton,
  Tooltip as MUITooltip,
} from "@mui/material";
import { FiSearch, FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import { Formik } from "formik";
import * as Yup from "yup";

import "./Ammenties.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";
import toast from "react-hot-toast";

// ---- filters
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "commercial", label: "Commercial" },
  { key: "residential", label: "Residential" },
  { key: "plot", label: "Plot" },
  { key: "project", label: "Project" },
];

const schema = Yup.object({
  type: Yup.string().required("Type is required"),
  title: Yup.string()
    .trim()
    .min(2, "Too short")
    .required("Amenity name is required"),
});

function typeChipColor(type) {
  switch (type) {
    case "commercial":
      return "warning";
    case "residential":
      return "primary";
    case "plot":
      return "success";
    case "project":
      return "secondary";
    default:
      return "default";
  }
}

export default function Ammenities() {
  // ---- demo data (replace with API later)
  const [rows, setRows] = useState([]);

  const [activeTab, setActiveTab] = useState("all");
  const [q, setQ] = useState("");

  const [showModal, setShowModal] = useState(false);

  // Optional: simple edit mode (same modal)
  const [editing, setEditing] = useState(null); // row or null

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return rows
      .filter((r) => (activeTab === "all" ? true : r.type === activeTab))
      .filter((r) => {
        if (!needle) return true;
        return (
          r.title.toLowerCase().includes(needle) ||
          r.type.toLowerCase().includes(needle)
        );
      });
  }, [rows, activeTab, q]);

  const openAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setShowModal(true);
  };

  const removeRow = (id) => {
    // In real app -> confirm dialog + API call
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const saveAmenity = async (values) => {
    const data = {
      url: editing?.title
        ? apis().updateAmmenity + "?id=" + editing?._id
        : apis().addAmmenity,
      method: editing?.title ? "PUT" : "POST",
      body: values,
    };

    const result = await httpAction(data);
    if (result?.status) {
      toast.success(result?.message);
      if (editing?.title) {
        const findedIndex = rows.findIndex(
          (item) => item?._id === editing?._id
        );
        const copyAmmenities = [...rows];
        copyAmmenities.splice(findedIndex, 1, result?.ammenity);
        setRows(copyAmmenities);
      } else {
        setRows((prev) => [...prev, result?.ammenity]);
      }
    }
    setShowModal(false);
    setEditing(null);
  };

  const getList = async () => {
    const data = {
      url: apis().ammenityList + "?type=" + activeTab,
    };
    const result = await httpAction(data);
    if (result?.status) {
      setRows(result?.list);
    }
  };

  useEffect(() => {
    getList();
  }, [activeTab]);

  return (
    <Box className="amenitiesPage">
      <Box className="amenitiesHeader">
        <Box>
          <Typography className="amenitiesTitle">Amenities</Typography>
          <Typography className="amenitiesSub">
            Manage amenities used in property listings (Commercial, Residential,
            Plot, Project).
          </Typography>
        </Box>

        <MUIButton
          variant="contained"
          className="amenitiesAddBtn"
          startIcon={<FiPlus />}
          onClick={openAdd}
        >
          Add New Amenity
        </MUIButton>
      </Box>

      <Paper className="amenitiesCard" elevation={0}>
        <Box className="amenitiesToolbar">
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            className="amenitiesTabs"
          >
            {FILTER_TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} />
            ))}
          </Tabs>

          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search amenities..."
            size="small"
            className="amenitiesSearch"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box className="amenitiesTableWrap">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="th">Amenity</TableCell>
                <TableCell className="th">Type</TableCell>
                <TableCell className="th" align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="emptyRow">
                    No amenities found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.id} hover className="tr">
                    <TableCell className="tdTitle">
                      <Typography className="amenityName">{r.title}</Typography>
                      <Typography className="amenityMeta">
                        ID: {r._id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={r.type}
                        color={typeChipColor(r.type)}
                        className="typeChip"
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box className="actions">
                        <MUITooltip title="Edit">
                          <IconButton
                            size="small"
                            className="iconBtn"
                            onClick={() => openEdit(r)}
                          >
                            <FiEdit2 />
                          </IconButton>
                        </MUITooltip>

                        <MUITooltip title="Delete">
                          <IconButton
                            size="small"
                            className="iconBtn danger"
                            onClick={() => removeRow(r.id)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </MUITooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* React-Bootstrap Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditing(null);
        }}
        centered
      >
        <Formik
          enableReinitialize
          initialValues={{
            type: editing?.type || "",
            title: editing?.title || "",
          }}
          validationSchema={schema}
          onSubmit={(values, helpers) => {
            saveAmenity(values);
            helpers.setSubmitting(false);
          }}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {editing ? "Edit Amenity" : "Add New Amenity"}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <div className="modalField">
                  <label className="modalLabel">Type</label>
                  <select
                    className={`form-select ${
                      formik.touched.type && formik.errors.type
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("type")}
                  >
                    <option value="">Select type</option>
                    <option value="commercial">Commercial</option>
                    <option value="residential">Residential</option>
                    <option value="plot">Plot</option>
                    <option value="project">Project</option>
                  </select>
                  {formik.touched.type && formik.errors.type ? (
                    <div className="invalid-feedback">{formik.errors.type}</div>
                  ) : null}
                </div>

                <div className="modalField">
                  <label className="modalLabel">Amenity Name</label>
                  <input
                    type="text"
                    className={`form-control ${
                      formik.touched.title && formik.errors.title
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder="e.g. Parking"
                    {...formik.getFieldProps("title")}
                  />
                  {formik.touched.title && formik.errors.title ? (
                    <div className="invalid-feedback">
                      {formik.errors.title}
                    </div>
                  ) : null}
                </div>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditing(null);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  Save
                </Button>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>
    </Box>
  );
}
